import React, { useEffect, useMemo, useState } from "react";
import { Banner, LegacyStack, Text } from "@shopify/polaris";
import { ClockMajor } from "@shopify/polaris-icons";
import styled from "styled-components";

import { getFromLocalStorage, setInLocalStorage } from "@smartrr/shared/utils/localStorageAccess";

import { ThemingStoreAccess } from "@vendor-app/app/AdminRoute/AdminThemingRoute/store";
import { useMyShopifyDomainSelector } from "@vendor-app/app/_state/reducers/shopify";

const PasswordlessLoginWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  .Polaris-Banner {
    margin: 1rem auto;
    max-width: max(60rem, 94%);
  }

  @media screen and (max-width: 1400px) {
    .Polaris-Banner {
      max-width: max(50rem, 85%);
    }
  }
`;

export const PasswordlessLoginMessage = () => {
  // Using local storage to not display banner after it is dismissed
  const myShopifyDomain = useMyShopifyDomainSelector();
  const localStorageKey = `${myShopifyDomain}:showPasswordlessWarningBanner`;
  const localStorageEntry = useMemo(() => getFromLocalStorage(localStorageKey), [localStorageKey]);

  const fetchTheme = ThemingStoreAccess.useActions().get;
  const themeValues = ThemingStoreAccess.useCurrentThemeValues();

  const [showBanner, setShowBanner] = useState<boolean>(true);
  const hideBanner = useMemo(
    () => !themeValues?.isPasswordless || !showBanner || localStorageEntry === "false",
    [themeValues?.isPasswordless, showBanner, localStorageEntry]
  );

  useEffect(() => {
    void fetchTheme();
  }, []);

  if (hideBanner) {
    return <React.Fragment />;
  }

  return (
    <PasswordlessLoginWrapper>
      <LegacyStack alignment="center" distribution="center">
        <Banner
          title="Warning"
          status="warning"
          icon={ClockMajor}
          onDismiss={() => {
            setShowBanner(false);
            setInLocalStorage(localStorageKey, "false");
          }}
        >
          <Text variant="bodyMd" as="p" fontWeight="regular">
            We detected that you still have Passwordless Login enabled. This feature will be deprecated on Dec 31,
            2025. To prevent issues with your Account Portal,
            <a data-testid="passwordless-login-support-link" href="mailto:support@smartrr.com">
              &nbsp;please contact support.
            </a>
          </Text>
        </Banner>
      </LegacyStack>
    </PasswordlessLoginWrapper>
  );
};
